'use client';

import { useEffect, useRef, useState } from 'react';
import { debounce } from '@bts-web/utils-functions';
import { getFiatTransactionFee } from '../../../common/gqlActions/getFiatTransactionFee.action';
import { useHandleGqlErrorsWithSnackbar } from '../../../common/utils/errors/useHandleGqlErrorsWithSnackbar';
import { FiatTransactionType } from '@bts-web/data-layer/server';

const DEBOUNCE_DELAY_AMOUNT_MS = 500;

interface FiatTransactionFeeState {
  fee: string;
  isLoading: boolean;
  hasError: boolean;
}

export const useFiatTransactionFee = (
  amountInput: string,
  fiatTransactionType: FiatTransactionType = 'DEPOSIT',
  minimumAmount: string,
): FiatTransactionFeeState => {
  const [fee, setFee] = useState('0');

  const [isLoading, setIsLoading] = useState(false);

  const [hasError, setHasError] = useState(false);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const processErrorsRef = useRef(processErrors);

  processErrorsRef.current = processErrors;

  const amount = Number(amountInput.replaceAll(',', '.'));

  const parsedMinimumAmount = Number(minimumAmount);

  const debouncedFetchRef = useRef(
    debounce(
      async (
        value: string,
        onDone: (result: FiatTransactionFeeState) => void,
      ) => {
        const response = await getFiatTransactionFee({
          amount: value,
          type: fiatTransactionType,
        });

        if (response.errors) {
          processErrorsRef.current(response.errors);

          onDone({ fee: '0', isLoading: false, hasError: true });
        } else {
          onDone({ fee: response.data, isLoading: false, hasError: false });
        }
      },
      DEBOUNCE_DELAY_AMOUNT_MS,
    ),
  );

  useEffect(() => {
    const debouncedFetch = debouncedFetchRef.current;

    if (!(amount > 0) || amount < parsedMinimumAmount) {
      debouncedFetch.cancel();

      setFee('0');

      setIsLoading(false);

      setHasError(false);

      return;
    }

    let cancelled = false;

    setIsLoading(true);

    setHasError(false);

    debouncedFetch(String(amount), (result) => {
      if (cancelled) return;

      setFee(result.fee);

      setIsLoading(result.isLoading);

      setHasError(result.hasError);
    });

    return () => {
      cancelled = true;

      debouncedFetch.cancel();
    };
  }, [amount, parsedMinimumAmount]);

  return { fee, isLoading, hasError };
};
